import type { Locale } from "@tovi/shared";

/**
 * Reading a new order aloud (`STORE_SETTINGS_VOICE_BROADCAST`).
 *
 * A station with a terminal on a shelf does not watch it. The source ships
 * this as a store setting *(verified — "Voice Broadcast")* for exactly that
 * reason, and the server decides whether it fires: the event carries the
 * message key or it does not, so a merchant who has not bought the feature has
 * no terminal that can be talked into it.
 *
 * Web Speech, which every browser this runs in has and which needs no
 * dependency, no shell and no permission prompt. It is best-effort by design:
 * a device with no voice for the language, a tab that has not been touched
 * yet, or a platform that refuses — none of those is a reason to fail an order
 * that has already been taken. The station screen and the printer are the
 * things that must not fail; this is the one that is allowed to.
 */
export function speak(text: string, locale: Locale): void {
  const synth = typeof window === "undefined" ? undefined : window.speechSynthesis;
  if (!synth) return;

  try {
    const utterance = new SpeechSynthesisUtterance(text);
    // BCP-47 from the locale code. A device with no Khmer voice falls back to
    // its default one, which reads the Latin transliteration badly and is
    // still better than silence.
    utterance.lang = locale === "km" ? "km-KH" : locale === "zh" ? "zh-CN" : "en-US";
    // Slower than default: a terminal across a busy station is competing with
    // an extractor fan.
    utterance.rate = 0.9;
    synth.speak(utterance);
  } catch {
    // A platform that refuses to speak is not a failed order.
  }
}
